import { isWebExtension } from "@sharpana/shared/utils/web_environment";

interface ExtensionWebSocketTokenResponse {
  token: string;
  expires_at: string;
}

// Cache the token to avoid excessive fetching
let cachedToken: ExtensionWebSocketTokenResponse | null = null;
let tokenPromise: Promise<ExtensionWebSocketTokenResponse> | null = null;

// Import real token API from allauth client
import { getExtensionWsToken as fetchExtensionWsToken } from "@sharpana/auth/api_client/allauth";

/**
 * Get a websocket token from the extension context.
 * Uses the allauth API client and caches the result until it expires.
 */
export async function getExtensionWsToken(): Promise<ExtensionWebSocketTokenResponse> {
  // If a promise is already in-flight, return it to avoid duplicate calls
  if (tokenPromise) {
    return tokenPromise;
  }

  // If we have a valid (non-expired) cached token, return it immediately
  if (cachedToken && new Date(cachedToken.expires_at) > new Date()) {
    return cachedToken;
  }

  // Otherwise fetch a new token and cache it
  tokenPromise = (async () => {
    try {
      const data =
        (await fetchExtensionWsToken()) as ExtensionWebSocketTokenResponse;
      cachedToken = data;
      return data;
    } finally {
      // Reset tokenPromise so that subsequent callers can trigger a refresh after completion
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}

/**
 * Get a fully formed WebSocket URL with authentication if needed
 * @param baseUrl - The base WebSocket URL
 * @returns Promise that resolves to the authenticated WebSocket URL
 */
export async function getAuthenticatedWsUrl(baseUrl: string): Promise<string> {
  const isExtension = isWebExtension();

  // Handle extension mode which needs token authentication
  if (isExtension) {
    // Check if we have a valid cached token
    if (cachedToken && new Date(cachedToken.expires_at) > new Date()) {
      return `${baseUrl}?token=${cachedToken.token}`;
    }

    // Get a fresh token
    const tokenData = await getExtensionWsToken();
    return `${baseUrl}?token=${tokenData.token}`;
  }

  // Non-extension mode doesn't need token
  return baseUrl;
}

export function isAndroidRNWebView(): boolean {
  return (
    /Android/i.test(navigator.userAgent) &&
    typeof window.ReactNativeWebView !== "undefined"
  );
}

/**
 * Create a base WebSocket URL for the current environment
 * @returns The base WebSocket URL for the current environment
 */
export function getBaseSummarySessionWsUrl(): string {
  // The value for VITE_WS_HOST_ANDROID_DEV is typically "10.0.2.2:8080" (or whatever port you're using to run the web app locally)
  const host =
    import.meta.env.VITE_WS_HOST_ANDROID_DEV &&
    isAndroidRNWebView() &&
    (window.location.host.includes("localhost") ||
      window.location.host.includes("127.0.0.1"))
      ? import.meta.env.VITE_WS_HOST_ANDROID_DEV
      : window.location.host;

  return `${
    window.location.protocol === "http:" ? "ws://" : "wss://"
  }${host}/ws/session/`;
}
